import { r as requestIdleCallback, c as cancelIdleCallback } from "./utilities-D68o91ht.js";
class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');
      if (!(template instanceof HTMLTemplateElement)) {
        return;
      }
      const shadow = this.attachShadow({ mode: "open" });
      shadow.append(template.content.cloneNode(true));
      template.remove();
    }
  }
}
class MissingRefError extends Error {
  /**
   * @param {string} ref
   * @param {Component} component
   */
  constructor(ref, component) {
    super(`Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`);
  }
}
class Component extends DeclarativeShadowElement {
  /**
   * An object holding references to child elements with `ref` attributes.
   *
   * @type {RefsType<T>}
   */
  refs =
    /** @type {RefsType<T>} */
    {};
  /**
   * An array of required refs. If a ref is not found, an error will be thrown.
   *
   * @type {string[] | undefined}
   */
  requiredRefs;
  /**
   * Set to true if refs may change after initial render (e.g., dynamic content).
   * When false (default), MutationObserver is not started, improving performance.
   *
   * @type {boolean}
   */
  observeRefChanges = false;
  /**
   * AbortController for managing async operations. Automatically aborted on disconnect.
   * Use `this.signal` in fetch calls: `fetch(url, { signal: this.signal })`
   *
   * @type {AbortController | undefined}
   */
  #abortController;
  /**
   * Handle for the requestIdleCallback used to set up MutationObserver.
   * Stored so it can be cancelled if the component disconnects before the callback fires.
   *
   * @type {number | undefined}
   */
  #idleCallbackHandle;
  /**
   * Gets the root node of the component, which is either its shadow root or the component itself.
   *
   * @returns {(ShadowRoot | Component<T>)[]} The root nodes.
   */
  get roots() {
    return this.shadowRoot ? [this, this.shadowRoot] : [this];
  }
  /**
   * Gets an AbortSignal for managing async operations.
   * The signal is automatically aborted when the component disconnects.
   *
   * @returns {AbortSignal} The abort signal.
   * @example
   * async fetchData() {
   *   const response = await fetch(url, { signal: this.signal });
   * }
   */
  get signal() {
    if (!this.#abortController) {
      this.#abortController = new AbortController();
    }
    return this.#abortController.signal;
  }
  /**
   * Called when the element is connected to the document's DOM.
   *
   * Initializes event listeners and refs.
   */
  connectedCallback() {
    super.connectedCallback();
    registerEventListeners();
    this.#updateRefs();
    if (this.observeRefChanges) {
      this.#idleCallbackHandle = requestIdleCallback(() => {
        if (!this.isConnected) {
          return;
        }
        for (const root of this.roots) {
          this.#mutationObserver.observe(root, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["ref"],
            attributeOldValue: true,
          });
        }
      });
    }
  }
  /**
   * Called when the element is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    this.#mutationObserver.takeRecords();
    this.#updateRefs();
  }
  /**
   * Called when the element is disconnected from the document's DOM.
   *
   * Disconnects the mutation observer and aborts any pending async operations.
   */
  disconnectedCallback() {
    if (this.#idleCallbackHandle !== void 0) {
      cancelIdleCallback(this.#idleCallbackHandle);
      this.#idleCallbackHandle = void 0;
    }
    this.#mutationObserver.disconnect();
    this.#abortController?.abort();
    this.#abortController = void 0;
  }
  /**
   * Updates the `refs` object by querying all descendant elements with `ref` attributes and storing references to them.
   *
   * This method is called to keep the `refs` object in sync with the DOM.
   */
  #updateRefs() {
    const refs =
      /** @type any */
      {};
    const elements = this.roots.reduce(
      (acc, root) => {
        for (const element of root.querySelectorAll("[ref]")) {
          if (!this.#isDescendant(element)) {
            continue;
          }
          acc.add(element);
        }
        return acc;
      },
      /** @type {Set<Element>} */
      /* @__PURE__ */ new Set()
    );
    for (const ref of elements) {
      const refName = ref.getAttribute("ref") ?? "";
      const isArray = refName.endsWith("[]");
      const path = isArray ? refName.slice(0, -2) : refName;
      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : [];
        array.push(ref);
        refs[path] = array;
      } else {
        refs[path] = ref;
      }
    }
    if (this.requiredRefs?.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new MissingRefError(ref, this);
        }
      }
    }
    this.refs = /** @type {RefsType<T>} */ refs;
  }
  /**
   * MutationObserver instance to observe changes in the component's DOM subtree and update refs accordingly.
   *
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    if (
      mutations.some(
        (m) =>
          (m.type === "attributes" && this.#isDescendant(m.target)) ||
          (m.type === "childList" && [...m.addedNodes, ...m.removedNodes].some(this.#isDescendant))
      )
    ) {
      this.#updateRefs();
    }
  });
  /**
   * Checks if a given node is a descendant of this component.
   *
   * @param {Node} node - The node to check.
   * @returns {boolean} True if the node is a descendant of this component.
   */
  #isDescendant = (node) => getClosestComponent(getAncestor(node)) === this;
}
function getAncestor(node) {
  if (node.parentNode) {
    return node.parentNode;
  }
  const root = node.getRootNode();
  if (root instanceof ShadowRoot) {
    return root.host;
  }
  return null;
}
function getClosestComponent(node) {
  if (!node) {
    return null;
  }
  if (node instanceof Component) {
    return node;
  }
  if (node instanceof HTMLElement && node.tagName.toLowerCase().endsWith("-component")) {
    return node;
  }
  const ancestor = getAncestor(node);
  if (ancestor) {
    return getClosestComponent(ancestor);
  }
  return null;
}
let initialized = false;
function registerEventListeners() {
  if (initialized) {
    return;
  }
  initialized = true;
  const events = [
    "click",
    "change",
    "select",
    "focus",
    "blur",
    "submit",
    "input",
    "keydown",
    "keyup",
    "toggle",
  ];
  const shouldBubble = ["focus", "blur"];
  const expensiveEvents = ["pointerenter", "pointerleave"];
  for (const eventName of [...events, ...expensiveEvents]) {
    const attribute = `on:${eventName}`;
    document.addEventListener(
      eventName,
      (event) => {
        const element = getElement(event);
        if (!element) {
          return;
        }
        const proxiedEvent =
          event.target !== element
            ? new Proxy(event, {
                get(target, property) {
                  if (property === "target") {
                    return element;
                  }
                  const value2 = Reflect.get(target, property);
                  if (typeof value2 === "function") {
                    return value2.bind(target);
                  }
                  return value2;
                },
              })
            : event;
        const value = element.getAttribute(attribute) ?? "";
        const parts = value.split("/");
        const selector = parts[0];
        let method = parts[1];
        const matches = value.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
        const data = matches ? matches[2] : null;
        const instance =
          selector && selector !== ""
            ? selector.startsWith("#")
              ? document.querySelector(selector)
              : element.closest(selector)
            : getClosestComponent(element);
        if (!instance || !method) {
          return;
        }
        const resolvedInstance =
          instance instanceof Component ? instance : getClosestComponent(instance);
        if (!resolvedInstance || !(resolvedInstance instanceof Component) || !method) {
          return;
        }
        method = method.replace(/\?.*/, "");
        const callback =
          /** @type {any} */
          resolvedInstance[method];
        if (typeof callback === "function") {
          try {
            const args = [proxiedEvent];
            if (data) {
              args.unshift(parseData(data));
            }
            callback.call(resolvedInstance, ...args);
          } catch (error) {
            console.error(error);
          }
        }
      },
      { capture: true }
    );
  }
  function getElement(event) {
    const target = event.composedPath?.()[0] ?? event.target;
    if (!(target instanceof Element)) {
      return;
    }
    if (target.hasAttribute(`on:${event.type}`)) {
      return target;
    }
    if (expensiveEvents.includes(event.type)) {
      return null;
    }
    return event.bubbles || shouldBubble.includes(event.type)
      ? target.closest(`[on\\:${event.type}]`)
      : null;
  }
}
function parseData(str) {
  const delimiter = str[0];
  const data = str.slice(1);
  return delimiter === "?"
    ? Object.fromEntries(
        Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [
          key,
          parseValue(value),
        ])
      )
    : parseValue(data);
}
function parseValue(str) {
  if (str === "true") {
    return true;
  }
  if (str === "false") {
    return false;
  }
  const maybeNumber = Number(str);
  if (!isNaN(maybeNumber) && str.trim() !== "") {
    return maybeNumber;
  }
  return str;
}
export { Component as C };
